import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Paper, Typography, Box, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StudentForm from './StudentForm';
import { studentsApi } from './api';

// Helper to remove empty strings/nulls to prevent backend validation errors
const cleanEmptyFields = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(v => cleanEmptyFields(v));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      // Keep booleans (false is valid), numbers (0 is valid)
      // Remove empty strings, null, undefined
      if (value === '' || value === null || value === undefined) {
        return acc;
      }
      acc[key] = cleanEmptyFields(value);
      return acc;
    }, {} as any);
  }
  return obj;
};

const StudentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const normalizeStudentPayload = (values: any) => {
    return {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      dateOfBirth: values.dateOfBirth,
      placeOfBirth: values.placeOfBirth,
      gender: values.gender,
      bloodGroupId: values.bloodGroupId,
      examBoardId: values.examBoardId,
      aadharNumber: values.aadharNumber,
      status: values.status,
      admissionNumber: values.admissionNumber,
      scholarNumber: values.scholarNumber,
      classId: values.classId,
      sectionId: values.sectionId,
      academicSessionId: values.academicSessionId,
      admissionDate: values.admissionDate,
      mediumId: values.mediumId,
      rte: values.rte,
      bpl: values.bpl,
      casteId: values.casteId,
      categoryId: values.categoryId,
      religionId: values.religionId,
      houseId: values.houseId,
      nationalityId: values.nationalityId,
      motherTongueId: values.motherTongueId,
      studentTypeId: values.studentTypeId,
      studentPhotoUrl: values.studentPhotoUrl,
      fatherPhotoUrl: values.fatherPhotoUrl,
      motherPhotoUrl: values.motherPhotoUrl,
      guardianPhotoUrl: values.guardianPhotoUrl,
      remarks: values.remarks,
      isDeclared: values.isDeclared,
      isPermanentSameAsPresent: values.isPermanentSameAsPresent,

      parent: values.parent ? {
        fatherName: values.parent.fatherName,
        fatherMobile: values.parent.fatherMobile,
        fatherOccupationId: values.parent.fatherOccupationId,
        fatherQualification: values.parent.fatherQualification,
        fatherAnnualIncome: values.parent.fatherAnnualIncome,
        motherName: values.parent.motherName,
        motherMobile: values.parent.motherMobile,
        motherOccupationId: values.parent.motherOccupationId,
        motherQualification: values.parent.motherQualification,
        motherAnnualIncome: values.parent.motherAnnualIncome,
        guardianName: values.parent.guardianName,
        guardianMobile: values.parent.guardianMobile,
        guardianRelationId: values.parent.guardianRelationId,
        guardianAddress: values.parent.guardianAddress,
        guardianEmail: values.parent.guardianEmail,
      } : undefined,

      addresses: values.addresses?.map((a: any) => ({
        type: a.type,
        addressLine1: a.addressLine1,
        addressLine2: a.addressLine2,
        city: a.city,
        state: a.state,
        pincode: a.pincode,
        district: a.district,
        country: a.country
      })),

      previousSchool: values.previousSchool ? {
        ...values.previousSchool
      } : undefined,

      documents: values.documents?.map((d: any) => ({
        documentId: d.documentId,
        name: d.name,
        url: d.url
      }))
    };
  };

  useEffect(() => {
    if (id) {
      fetchStudent(id);
    }
  }, [id]);

  const fetchStudent = async (studentId: string) => {
    try {
      setLoading(true);
      const data = await studentsApi.getById(studentId);

      if (!data) {
        alert("Student not found");
        navigate("/scholar-register/students");
        return;
      }

      const mapStudentToForm = (data: any) => {
        return {
          ...data,
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          admissionDate: data.admissionDate ? data.admissionDate.split('T')[0] : '',
          previousSchool: data.previousSchool ? {
            schoolName: data.previousSchool.schoolName || '',
            boardId: data.previousSchool.boardId || data.previousSchool.board?.id || '',
            passingYear: data.previousSchool.passingYear || '',
            previousClass: data.previousSchool.previousClass || '',
            mediumId: data.previousSchool.mediumId || data.previousSchool.medium?.id || '',
            tcNumber: data.previousSchool.tcNumber || '',
            tcDate: data.previousSchool?.tcDate ? data.previousSchool.tcDate.split('T')[0] : '',
            result: data.previousSchool.result || '',
            leavingReasonId: data.previousSchool.leavingReasonId || data.previousSchool.leavingReason?.id || ''
          } : null,
          addresses: [
            data.addresses?.find((a: any) => a.type === 'PRESENT') || {
              type: 'PRESENT',
              addressLine1: '',
              addressLine2: '',
              city: '',
              district: '',
              state: '',
              pincode: '',
              country: 'India'
            },
            data.addresses?.find((a: any) => a.type === 'PERMANENT') || {
              type: 'PERMANENT',
              addressLine1: '',
              addressLine2: '',
              city: '',
              district: '',
              state: '',
              pincode: '',
              country: 'India'
            }
          ],
          isPermanentSameAsPresent: data.isPermanentSameAsPresent || false,
          isDeclared: data.isDeclared || false,
          documents: data.documents?.map((doc: any) => ({
            documentId: doc.documentId || doc.document?.id,
            name: doc.name,
            url: doc.url
          })) || []
        };
      };

      const mapped = mapStudentToForm(data);
      setStudent(mapped);
    } catch (error) {
      alert('Failed to fetch student details');
      navigate('/scholar-register/students');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!id) return;

    // 1. Normalize and Clean Payload
    const normalizedPayload = normalizeStudentPayload(values);
    const cleanValues = cleanEmptyFields(normalizedPayload);

    try {
      await studentsApi.update(id, cleanValues);
      navigate('/scholar-register/students');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update student';
      alert(`Error: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/scholar-register/students')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Edit Student
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <StudentForm initialValues={student} onSubmit={handleSubmit} isEdit />
      </Paper>
    </Container>
  );
};

export default StudentEdit;
